import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  CheckCircle as ValidateIcon,
  Cancel as RejectIcon,
  HelpOutline as JustifyIcon,
} from '@mui/icons-material';
import { congesAPI } from '../../../services/api';
import {
  Conge,
  getTypeCongeLabel,
  getStatutCongeLabel,
  getStatutCongeColor,
} from '../../../types/conge';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';

const HR_COLOR = '#00897B';

interface CongeValidationDialogProps {
  open: boolean;
  onClose: () => void;
  conge: Conge | null;
  onSuccess: () => void;
}

const CongeValidationDialog: React.FC<CongeValidationDialogProps> = ({
  open,
  onClose,
  conge,
  onSuccess,
}) => {
  const [decision, setDecision] = useState<'VALIDEE' | 'REFUSEE' | 'JUSTIFIER'>('VALIDEE');
  const [commentaire, setCommentaire] = useState('');
  const [loading, setLoading] = useState(false);

  const { showSuccess, showError } = useAlert();

  const handleSubmit = async () => {
    if (!conge) return;

    setLoading(true);
    try {
      await congesAPI.validate(conge.id, {
        statut: decision,
        commentaire_rh: commentaire || undefined,
      });

      const statusLabel = decision === 'VALIDEE' ? 'validée' : decision === 'REFUSEE' ? 'refusée' : 'mise en attente de justification';
      showSuccess(`Demande de congé ${statusLabel} avec succès`);

      onSuccess();
      onClose();

      // Reset form
      setDecision('VALIDEE');
      setCommentaire('');
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (!conge) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        Validation de la Demande de Congé
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Request Summary */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: HR_COLOR }}>
            Résumé de la Demande
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Employé:</strong> {conge.employee?.nom_complet || 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {conge.employee?.matricule} • {conge.employee?.fonction || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Type:</strong>{' '}
                <Chip
                  label={getTypeCongeLabel(conge.type_conge)}
                  size="small"
                  sx={{ bgcolor: HR_COLOR, color: 'white' }}
                />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Jours:</strong> {conge.nbr_jours}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Du:</strong> {formatDate(conge.date_debut)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Au:</strong> {formatDate(conge.date_fin)}
              </Typography>
            </Grid>
            {conge.motif && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Motif:</strong> {conge.motif}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Statut actuel:</strong>{' '}
                <Chip
                  label={getStatutCongeLabel(conge.statut)}
                  size="small"
                  sx={{ bgcolor: getStatutCongeColor(conge.statut), color: 'white' }}
                />
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Decision */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ color: HR_COLOR, fontWeight: 'bold' }}>
            Décision
          </FormLabel>
          <RadioGroup
            value={decision}
            onChange={(e) => setDecision(e.target.value as 'VALIDEE' | 'REFUSEE' | 'JUSTIFIER')}
          >
            <FormControlLabel
              value="VALIDEE"
              control={<Radio sx={{ color: '#66BB6A', '&.Mui-checked': { color: '#66BB6A' } }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ValidateIcon sx={{ color: '#66BB6A' }} />
                  <Typography>Valider la demande</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="REFUSEE"
              control={<Radio sx={{ color: '#EF5350', '&.Mui-checked': { color: '#EF5350' } }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RejectIcon sx={{ color: '#EF5350' }} />
                  <Typography>Refuser la demande</Typography>
                </Box>
              }
            />
            {conge.type_conge === 'CM' && (
              <FormControlLabel
                value="JUSTIFIER"
                control={<Radio sx={{ color: '#42A5F5', '&.Mui-checked': { color: '#42A5F5' } }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <JustifyIcon sx={{ color: '#42A5F5' }} />
                    <Typography>Demander justificatif médical</Typography>
                  </Box>
                }
              />
            )}
          </RadioGroup>
        </FormControl>

        {/* Comment */}
        <TextField
          label="Commentaire RH (optionnel)"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder={
            decision === 'REFUSEE'
              ? 'Raison du refus...'
              : decision === 'JUSTIFIER'
              ? 'Documents à fournir...'
              : 'Commentaire optionnel...'
          }
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            bgcolor:
              decision === 'VALIDEE'
                ? '#66BB6A'
                : decision === 'REFUSEE'
                ? '#EF5350'
                : '#42A5F5',
            '&:hover': {
              bgcolor:
                decision === 'VALIDEE'
                  ? '#4CAF50'
                  : decision === 'REFUSEE'
                  ? '#E53935'
                  : '#1E88E5',
            },
          }}
          startIcon={
            decision === 'VALIDEE' ? (
              <ValidateIcon />
            ) : decision === 'REFUSEE' ? (
              <RejectIcon />
            ) : (
              <JustifyIcon />
            )
          }
        >
          {loading
            ? 'Enregistrement...'
            : decision === 'VALIDEE'
            ? 'Valider'
            : decision === 'REFUSEE'
            ? 'Refuser'
            : 'Demander Justificatif'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CongeValidationDialog;
